import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  isChatEscalation,
  type HandoffDto,
  type HandoffPriority,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import {
  acknowledgeHandoff,
  listHandoffQueue,
  resolveHandoff,
  startHandoff,
} from "@/lib/handoff-api";
import { handoffSenderLine } from "@/lib/handoff-labels";
import { PATIENT_OVERVIEW_LABEL } from "@/lib/work-item-cta";
import { SupportEscalationMessages } from "@/components/SupportEscalationMessages";
import { useReadOnly, useViewPath } from "@/components/SupportView";

// TES-292 [J20] — the destination-role handoff / escalation queue. A clinician
// works the open handoffs addressed to their role: acknowledge (take ownership),
// start (in_progress), resolve (close, optionally with a note). Defaults to the
// caller's own role queue (server-derived).

const PRIORITY_LABEL: Record<HandoffPriority, string> = {
  low: "Låg",
  medium: "Medel",
  high: "Hög",
  urgent: "Akut",
};

const PRIORITY_CLASS: Record<HandoffPriority, string> = {
  low: "bg-surfaceMuted text-textSecondary",
  medium: "bg-brand/10 text-brand",
  high: "bg-warning/15 text-warning",
  urgent: "bg-danger/10 text-danger",
};

const STATUS_LABEL: Record<string, string> = {
  sent: "Skickad",
  acknowledged: "Mottagen",
  in_progress: "Pågår",
  resolved: "Löst",
};

const ROLE_LABEL: Record<string, string> = {
  coach: "Coach",
  doctor: "Läkare",
  // TES-1110 — an admin's handoff is an escalation from Ella Support.
  admin: "Ella Support",
  system: "System",
};

export function HandoffQueueRoute() {
  const { signOut } = useAuth();
  const readOnly = useReadOnly();
  const viewPath = useViewPath();
  const [rows, setRows] = useState<HandoffDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const handleError = useCallback(
    (e: unknown) => {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    },
    [signOut],
  );

  const reload = useCallback(() => {
    listHandoffQueue().then(setRows).catch(handleError);
  }, [handleError]);

  useEffect(reload, [reload]);

  async function act(id: string, fn: () => Promise<unknown>): Promise<void> {
    setBusy(id);
    setError(null);
    try {
      await fn();
      reload();
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(null);
    }
  }

  async function onResolve(id: string): Promise<void> {
    const note = window.prompt("Lösning (valfritt):") ?? undefined;
    await act(id, () => resolveHandoff(id, note?.trim() || undefined));
  }

  return (
    <div className="flex flex-col gap-md">
      <h1 className="font-display text-3xl tracking-tight">Överlämningar</h1>
      <p className="max-w-2xl text-sm text-textSecondary">
        Öppna överlämningar och eskaleringar till din roll. Ta emot, påbörja och
        lös ärenden — varje steg loggas i journalspåret.
      </p>

      {error ? (
        <div className="rounded-md border border-danger/30 bg-danger/10 p-md text-sm text-danger">
          {error}
        </div>
      ) : null}

      {rows && rows.length === 0 ? (
        <div className="rounded-md border border-surfaceMuted bg-surface p-md text-textSecondary">
          Inga öppna överlämningar.
        </div>
      ) : null}

      {rows?.map((h) => (
        <div
          key={h.id}
          className="flex flex-col gap-sm rounded-md border border-surfaceMuted bg-surface p-md"
        >
          <div className="flex flex-wrap items-center gap-sm text-xs text-textSecondary">
            <span
              className={`rounded-pill px-sm py-xs ${PRIORITY_CLASS[h.priority]}`}
            >
              {PRIORITY_LABEL[h.priority]}
            </span>
            <span className="rounded-pill bg-surfaceMuted px-sm py-xs">
              {ROLE_LABEL[h.fromRole] ?? h.fromRole} →{" "}
              {ROLE_LABEL[h.toRole] ?? h.toRole}
            </span>
            <span className="rounded-pill bg-surfaceMuted px-sm py-xs">
              {h.status === "resolved" && isChatEscalation(h)
                ? "Bedömt av läkare"
                : (STATUS_LABEL[h.status] ?? h.status)}
            </span>
            {/* TES-1034 (Emelie's point 8) — the patient is one click away,
                on her page with the handoff open (#handoff). */}
            <Link
              to={viewPath(
                `/patients/${encodeURIComponent(h.patientId)}?flik=oversikt#handoff`,
              )}
              className="text-brand underline"
            >
              {PATIENT_OVERVIEW_LABEL}
            </Link>
            {h.deadline ? <span>senast {h.deadline.slice(0, 10)}</span> : null}
          </div>
          <p className="text-xs text-textSecondary">{handoffSenderLine(h)}</p>
          <p className="text-sm text-textPrimary">{h.reason}</p>
          {h.context ? (
            <p className="text-sm text-textSecondary">{h.context}</p>
          ) : null}
          {h.requestedDecision ? (
            <p className="text-sm text-textSecondary">
              <span className="font-semibold">Efterfrågat beslut: </span>
              {h.requestedDecision}
            </p>
          ) : null}
          {/* TES-1110 — escalated from Ella Support: the message is linked. */}
          <SupportEscalationMessages handoff={h} readOnly={readOnly} />

          {/* TES-1040 — none of the three in the admin's read-only view. */}
          {readOnly ? null : (
            <div className="flex items-center gap-sm">
              {h.status === "sent" ? (
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => void act(h.id, () => acknowledgeHandoff(h.id))}
                  className="rounded-md border border-surfaceMuted px-md py-sm text-sm text-textSecondary hover:text-textPrimary disabled:opacity-50"
                >
                  Ta emot
                </button>
              ) : null}
              {h.status !== "in_progress" ? (
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => void act(h.id, () => startHandoff(h.id))}
                  className="rounded-md border border-surfaceMuted px-md py-sm text-sm text-textSecondary hover:text-textPrimary disabled:opacity-50"
                >
                  Påbörja
                </button>
              ) : null}
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => void onResolve(h.id)}
                className="ml-auto rounded-md bg-brand px-md py-sm text-sm text-white hover:opacity-90 disabled:opacity-50"
              >
                Lös
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
